"use client";

import { ChevronDown, Plus } from "lucide-react";
import { CitationTool } from "@/components/citation-tool";
import { useBibStrings } from "@/components/citation-tool/tool-strings";
import { Button } from "@/components/ui/button";
import { getFeaturedStyles } from "@/data/citation-styles";
import type { Bibliography } from "@/lib/bibliography";
import { cn } from "@/lib/utils";

interface AddSourcePanelProps {
  open: boolean;
  onToggle: () => void;
  project: Bibliography;
  /** The style the project is mostly in, so new sources match by default. */
  defaultStyle: string;
  defaultOutputLocale?: string;
  /** A `?source=` deep link; runs one citation on mount when present. */
  source?: string;
  onSaved: () => void;
}

/**
 * The generator, inside the studio. Collapsed it is a one-line "Add a source"
 * affordance at the top of the workspace (Linear's new-item bar); expanded it is the
 * full auto-detect / book search / article search / manual-entry tool, keyed by
 * project so switching projects resets the style default.
 */
export function AddSourcePanel({
  open,
  onToggle,
  project,
  defaultStyle,
  defaultOutputLocale,
  source,
  onSaved,
}: AddSourcePanelProps) {
  const bib = useBibStrings();
  return (
    <section
      aria-labelledby="studio-add-source"
      className={cn(
        "rounded-xl border transition-colors",
        open
          ? "border-border bg-background shadow-soft"
          : "border-border/80 border-dashed bg-muted/20 hover:border-border hover:bg-muted/40"
      )}
    >
      <button
        aria-controls="studio-add-source-body"
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-4 py-3 text-start"
        onClick={onToggle}
        type="button"
      >
        <span
          className={cn(
            "flex size-8 shrink-0 items-center justify-center rounded-full",
            open
              ? "bg-primary text-primary-foreground"
              : "bg-background text-foreground shadow-soft"
          )}
        >
          <Plus className="size-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span
            className="block font-medium text-foreground text-sm"
            id="studio-add-source"
          >
            {bib.studioAddSource}
          </span>
          {open ? null : (
            <span className="block truncate text-muted-foreground text-xs">
              {bib.studioAddSourceHint}
            </span>
          )}
        </span>
        {open ? (
          <Button asChild size="xs" variant="ghost">
            <span>{bib.studioDoneAdding}</span>
          </Button>
        ) : (
          <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
        )}
      </button>
      {open ? (
        <div
          className="border-border/70 border-t px-4 py-5 sm:px-5"
          id="studio-add-source-body"
        >
          <p className="mb-5 text-muted-foreground text-sm">
            {bib.studioAddSourceHint}
          </p>
          <CitationTool
            defaultOutputLocale={defaultOutputLocale}
            defaultStyle={defaultStyle}
            inStudio
            key={project.id}
            onCitationSaved={onSaved}
            source={source}
            styles={getFeaturedStyles()}
          />
        </div>
      ) : null}
    </section>
  );
}
